import React, { useEffect, useState } from "react";
import FHIR from "fhirclient";
import "bootstrap/dist/css/bootstrap.min.css";
import BoxContainer from "./containers/BoxContainer";
import CommunicationService from "./services/CommunicationService";
import { loadOrgConfig } from "./configs/loadConfig";
import { Modal, Button } from "react-bootstrap";
import { logAccessEvent } from "./services/logService";
import { useDispatch, useSelector, useStore } from "react-redux";
import { setOrgConfig, setAuthToken } from "./slices/configSlice";
import { decodeJWT } from "./utils/jwtUtils";
import { loadSmartStyles } from "./styleLoader";
import { syncProvidersToServer } from "./store/thunks/syncProvidersToServer";

function App() {
  const dispatch = useDispatch();
  const store = useStore();

  const orgConfig = useSelector(
    (state) => state.config.orgConfig
  );

  const [contextPatient, setContextPatient] = useState(null);
  const [contextProvider, setContextProvider] = useState(null);
  const [contextEncounter, setContextEncounter] = useState(null);

  const [error, setError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [jwtData, setJwtData] = useState(null);
  const [fhirClient, setFhirClient] = useState(null);
  const [communicationService, setCommunicationService] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function initializeApp() {
      console.log("RAW URL:", window.location.href);

      function getEnvironmentFromHostname() {
        const host = window.location.hostname;

        if (host.includes("dev.endpointhealth.ca")) {
          return "dev";
        }

        if (host.includes("uat.endpointhealth.ca")) {
          return "uat";
        }

        if (host.includes("launch.endpointhealth.ca")) {
          return "prd";
        }

        if (host === "localhost" || host.endsWith(".local")) {
          return "dev";
        }

        return "dev";
      }

      const environment = getEnvironmentFromHostname();

      console.log("Environment:", environment);

      if (environment === "uat" || environment === "prd") {
        console.log = () => {};
        console.warn = () => {};
        console.info = () => {};
      }

      const searchParams = new URLSearchParams(
        window.location.search
      );

      let issParam = searchParams.get("iss");
      let launch = searchParams.get("launch");

      const launchError = searchParams.get("error");
      const errorDescription =
        searchParams.get("error_description");

      const appParam = searchParams.get("app");

      if (appParam && (!issParam || !launch)) {
        try {
          const questionMarkIndex = appParam.indexOf("?");

          if (questionMarkIndex >= 0) {
            const nestedParams = new URLSearchParams(
              appParam.substring(questionMarkIndex + 1)
            );

            issParam =
              issParam || nestedParams.get("iss");

            launch =
              launch || nestedParams.get("launch");
          }
        } catch (err) {
          console.warn(
            "Failed to parse nested SMART parameters from app:",
            err
          );
        }
      }

      if (issParam && launch) {
        sessionStorage.setItem("smart_iss", issParam);
        sessionStorage.setItem("smart_launch", launch);
      } else {
        issParam = sessionStorage.getItem("smart_iss");
        launch = sessionStorage.getItem("smart_launch");
      }

      if (launchError) {
        const readable = decodeURIComponent(
          errorDescription || launchError
        );

        if (cancelled) {
          return;
        }

        setError(
          "Authorization failed: " + readable
        );

        setShowErrorModal(true);

        return;
      }

      if (!issParam) {
        if (cancelled) {
          return;
        }

        setError(
          "Missing SMART issuer (iss) parameter."
        );

        setShowErrorModal(true);

        return;
      }

      let config;

      try {
        config = await loadOrgConfig(issParam);

        if (cancelled) {
          return;
        }

        console.log(
          "Loaded org config:",
          config
        );

        if (!config) {
          throw new Error(
            "No configuration was returned."
          );
        }

        const clientId = config.clientId;

        const scope = Array.isArray(config.scopes)
          ? config.scopes.join(" ")
          : config.scopes;

        if (!clientId) {
          throw new Error(
            "Configuration does not contain a SMART client ID."
          );
        }

        if (!scope) {
          throw new Error(
            "Configuration does not contain SMART scopes."
          );
        }

        console.log("ClientID:", clientId);
        console.log("Scope:", scope);

        dispatch(setOrgConfig(config));

        async function fetchContextData() {
          try {
            const client = await FHIR.oauth2.ready();

            if (cancelled) {
              return;
            }

            setFhirClient(client);

            const tokenResponse =
              client?.state?.tokenResponse || {};

            const accessToken =
              tokenResponse.access_token;

            const idToken =
              tokenResponse.id_token;

            const decodedAccessToken =
              accessToken
                ? decodeJWT(accessToken)
                : null;

            const decodedIdToken =
              idToken
                ? decodeJWT(idToken)
                : null;

            setJwtData({
              access: decodedAccessToken,
              id: decodedIdToken
            });

            if (tokenResponse.smart_style_url) {
              loadSmartStyles(
                tokenResponse.smart_style_url
              );
            }

            const practitionerId =
              decodedIdToken?.fhirUser?.split("/")[1] ||
              decodedAccessToken?.fhirUser?.split("/")[1] ||
              null;

            const encounterId =
              tokenResponse.encounter;

            const practitioner =
              practitionerId
                ? await client.request(
                    "Practitioner/" +
                      practitionerId
                  )
                : null;

            const patient =
              await client.patient.read();

            const encounter =
              encounterId
                ? await client.request(
                    "Encounter/" +
                      encounterId
                  )
                : null;

            if (cancelled) {
              return;
            }

            setContextProvider(practitioner);
            setContextPatient(patient);

            if (encounter) {
              setContextEncounter(encounter);
            }

            if (accessToken) {
              logAccessEvent({
                practitioner,
                patient,
                store,
                accessToken
              });

              dispatch(
                setAuthToken(accessToken)
              );
            } else {
              console.warn(
                "No access token, unable to access logger"
              );
            }
          } catch (error) {
            if (cancelled) {
              return;
            }

            console.error(
              "Error Fetching FHIR Data:",
              error
            );

            setError(
              "FHIR Initialization Error: " +
                error.message
            );

            setShowErrorModal(true);
          }
        }

        try {
          const client =
            await FHIR.oauth2.ready();

          if (cancelled) {
            return;
          }

          const tokenResponse =
            client?.state?.tokenResponse;

          if (!tokenResponse?.access_token) {
            setError(
              "Token exchange failed - no access token received."
            );

            setShowErrorModal(true);

            return;
          }

          await fetchContextData();
        } catch (error) {
          if (cancelled) {
            return;
          }

          console.log(
            "SMART client not ready - initiating launch"
          );

          if (issParam && launch) {
            const redirectUri =
              window.location.origin;

            FHIR.oauth2.authorize({
              clientId,
              redirectUri,
              scope,
              iss: issParam,
              launch,
              aud: issParam,
              completeInTarget: true
            });
          } else {
            setError(
              "Missing SMART launch parameters."
            );

            setShowErrorModal(true);
          }
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error(
          "Configuration initialization failed:",
          error
        );

        setError(
          "Unable to load pCom configuration. " +
            "Please try again in a few seconds."
        );

        setShowErrorModal(true);
      }
    }

    initializeApp();

    return () => {
      cancelled = true;
    };
  }, [dispatch, store]);

  useEffect(() => {
    if (!fhirClient) {
      return;
    }

    setCommunicationService(
      new CommunicationService(fhirClient)
    );
  }, [fhirClient]);

  useEffect(() => {
    const interval = setInterval(() => {
      console.log(
        "Dispatching periodic provider sync"
      );

      dispatch(
        syncProvidersToServer(store)
      );
    }, 10 * 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [dispatch, store]);

  return (
    <div
      style={{
        padding: "0px 5px 5px 5px",
        fontFamily: "Arial, sans-serif"
      }}
    >
      {fhirClient &&
      communicationService &&
      orgConfig ? (
        <BoxContainer
          contextProvider={contextProvider}
          contextPatient={contextPatient}
          contextEncounter={contextEncounter}
          error={error}
          fhirClient={fhirClient}
          communicationService={
            communicationService
          }
          orgConfig={orgConfig}
        />
      ) : (
        <div>Loading pCom...</div>
      )}

      <Modal
        show={showErrorModal}
        onHide={() =>
          setShowErrorModal(false)
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            pCom Initialization Error
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              fontSize: "0.95rem"
            }}
          >
            {error ||
              "An unexpected error occurred. Please try again."}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setShowErrorModal(false)
            }
          >
            Dismiss
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default App;